/**
 * Weather Alert Service for HoneyDo Platform
 *
 * Monitors weather conditions and generates contextual alerts for users
 * based on their active outdoor tasks and upcoming weather forecasts.
 */

import { weatherSchedulingService, WeatherForecast } from './WeatherSchedulingService';
import { Task } from '../types/api';

export interface WeatherAlert {
  id: string;
  type: 'critical' | 'high' | 'medium' | 'low';
  severity: 'danger' | 'warning' | 'info';
  title: string;
  message: string;
  icon: 'rain' | 'sun' | 'wind' | 'temperature' | 'storm';
  affectedTaskIds: string[];
  affectedTaskCount: number;
  date: string;
  createdAt: string;
  expiresAt: string;
  dismissible: true;
  actionable: boolean;
  actions?: {
    label: string;
    action: 'view_tasks' | 'reschedule' | 'dismiss';
  }[];
}

export interface WeatherAlertContext {
  tasks: Task[];
  location: {
    zipCode?: string;
    latitude?: number;
    longitude?: number;
  };
  forecastDays?: number;
}

export interface AlertDetectionResult {
  alerts: WeatherAlert[];
  checkTimestamp: string;
  forecastUsed: WeatherForecast[];
}

class WeatherAlertService {
  private alertCache: Map<string, WeatherAlert[]> = new Map();
  private cacheExpiryMinutes = 30;
  private dismissedAlerts: Set<string> = new Set();

  constructor() {
    this.loadDismissedAlerts();
  }

  /**
   * Main method: Check weather and generate alerts for user's tasks
   */
  async checkWeatherAlerts(context: WeatherAlertContext): Promise<AlertDetectionResult> {
    try {
      const { tasks, location, forecastDays = 7 } = context;

      // Get weather forecast
      const forecast = await weatherSchedulingService.getWeatherForecast(
        location.zipCode,
        location.latitude,
        location.longitude,
        forecastDays
      );

      if (!forecast || forecast.length === 0) {
        return {
          alerts: [],
          checkTimestamp: new Date().toISOString(),
          forecastUsed: []
        };
      }

      const alerts: WeatherAlert[] = [];

      // Filter outdoor tasks only
      const outdoorTasks = this.filterOutdoorTasks(tasks);

      // Check for various alert conditions
      alerts.push(...this.checkRainAlerts(forecast, outdoorTasks));
      alerts.push(...this.checkTemperatureAlerts(forecast, outdoorTasks));
      alerts.push(...this.checkWindAlerts(forecast, outdoorTasks));
      alerts.push(...this.checkGoodWeatherWindows(forecast, outdoorTasks));

      // Filter out dismissed alerts
      const activeAlerts = alerts.filter(alert => !this.isAlertDismissed(alert.id));

      return {
        alerts: activeAlerts,
        checkTimestamp: new Date().toISOString(),
        forecastUsed: forecast
      };
    } catch (error) {
      console.error('[WeatherAlertService] Failed to check weather alerts:', error);
      return {
        alerts: [],
        checkTimestamp: new Date().toISOString(),
        forecastUsed: []
      };
    }
  }

  /**
   * Check for rain alerts affecting outdoor tasks
   */
  private checkRainAlerts(forecast: WeatherForecast[], outdoorTasks: Task[]): WeatherAlert[] {
    const alerts: WeatherAlert[] = [];
    const now = new Date();

    for (const day of forecast) {
      const forecastDate = new Date(day.date);
      const daysFromNow = Math.floor((forecastDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      // High rain probability alert
      if (day.rainChance > 60 && daysFromNow <= 2) {
        const affectedTasks = this.getTasksNearDate(outdoorTasks, day.date, 1);

        if (affectedTasks.length > 0) {
          const alertType = day.rainChance > 80 ? 'critical' : 'high';
          const severity = day.rainChance > 80 ? 'danger' : 'warning';

          alerts.push({
            id: `rain-${day.date}`,
            type: alertType,
            severity,
            title: daysFromNow === 0 ? 'Rain Expected Today' : `Rain Expected ${this.formatDaysFromNow(daysFromNow)}`,
            message: `${day.rainChance}% chance of rain - ${affectedTasks.length} outdoor task${affectedTasks.length > 1 ? 's' : ''} may be affected`,
            icon: 'rain',
            affectedTaskIds: affectedTasks.map(t => t.id),
            affectedTaskCount: affectedTasks.length,
            date: day.date,
            createdAt: new Date().toISOString(),
            expiresAt: new Date(forecastDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
            dismissible: true,
            actionable: true,
            actions: [
              { label: 'View Tasks', action: 'view_tasks' },
              { label: 'Reschedule', action: 'reschedule' }
            ]
          });
        }
      }
    }

    return alerts;
  }

  /**
   * Check for temperature extreme alerts
   */
  private checkTemperatureAlerts(forecast: WeatherForecast[], outdoorTasks: Task[]): WeatherAlert[] {
    const alerts: WeatherAlert[] = [];
    const now = new Date();

    for (const day of forecast) {
      const forecastDate = new Date(day.date);
      const daysFromNow = Math.floor((forecastDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysFromNow > 3) continue; // Only alert for next 3 days

      const affectedTasks = this.getTasksNearDate(outdoorTasks, day.date, 1);

      // Extreme heat alert
      if (day.tempHigh > 95 && affectedTasks.length > 0) {
        alerts.push({
          id: `heat-${day.date}`,
          type: day.tempHigh > 100 ? 'critical' : 'high',
          severity: day.tempHigh > 100 ? 'danger' : 'warning',
          title: 'Heat Advisory',
          message: `Temperature expected to reach ${Math.round(day.tempHigh)}°F ${this.formatDaysFromNow(daysFromNow)} - Avoid outdoor work during peak hours`,
          icon: 'temperature',
          affectedTaskIds: affectedTasks.map(t => t.id),
          affectedTaskCount: affectedTasks.length,
          date: day.date,
          createdAt: new Date().toISOString(),
          expiresAt: new Date(forecastDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
          dismissible: true,
          actionable: true,
          actions: [
              { label: 'View Tasks', action: 'view_tasks' },
              { label: 'Reschedule', action: 'reschedule' }
          ]
        });
      }

      // Extreme cold alert
      if (day.tempLow < 32 && affectedTasks.length > 0) {
        // Check for tasks that are affected by freezing temps (painting, plumbing, etc.)
        const coldSensitiveTasks = affectedTasks.filter(t =>
          ['painting', 'plumbing', 'exterior'].includes(t.category)
        );

        if (coldSensitiveTasks.length > 0) {
          alerts.push({
            id: `cold-${day.date}`,
            type: day.tempLow < 20 ? 'high' : 'medium',
            severity: day.tempLow < 20 ? 'warning' : 'info',
            title: 'Freezing Temperature Alert',
            message: `Temperature expected to drop to ${Math.round(day.tempLow)}°F ${this.formatDaysFromNow(daysFromNow)} - ${coldSensitiveTasks.length} task${coldSensitiveTasks.length > 1 ? 's' : ''} affected`,
            icon: 'temperature',
            affectedTaskIds: coldSensitiveTasks.map(t => t.id),
            affectedTaskCount: coldSensitiveTasks.length,
            date: day.date,
            createdAt: new Date().toISOString(),
            expiresAt: new Date(forecastDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
            dismissible: true,
            actionable: true,
            actions: [
              { label: 'View Tasks', action: 'view_tasks' }
            ]
          });
        }
      }
    }

    return alerts;
  }

  /**
   * Check for high wind alerts
   */
  private checkWindAlerts(forecast: WeatherForecast[], outdoorTasks: Task[]): WeatherAlert[] {
    const alerts: WeatherAlert[] = [];
    const now = new Date();

    for (const day of forecast) {
      const forecastDate = new Date(day.date);
      const daysFromNow = Math.floor((forecastDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysFromNow > 2) continue; // Only alert for next 2 days

      if (day.windSpeed > 20) {
        const affectedTasks = this.getTasksNearDate(outdoorTasks, day.date, 1);

        // Filter tasks affected by high winds (roof work, ladder work, etc.)
        const windSensitiveTasks = affectedTasks.filter(t =>
          t.description?.toLowerCase().includes('ladder') ||
          t.description?.toLowerCase().includes('roof') ||
          ['exterior', 'landscaping'].includes(t.category)
        );

        if (windSensitiveTasks.length > 0) {
          alerts.push({
            id: `wind-${day.date}`,
            type: day.windSpeed > 30 ? 'critical' : 'high',
            severity: day.windSpeed > 30 ? 'danger' : 'warning',
            title: 'High Wind Alert',
            message: `Winds up to ${Math.round(day.windSpeed)} mph expected ${this.formatDaysFromNow(daysFromNow)} - Postpone ladder work and outdoor tasks`,
            icon: 'wind',
            affectedTaskIds: windSensitiveTasks.map(t => t.id),
            affectedTaskCount: windSensitiveTasks.length,
            date: day.date,
            createdAt: new Date().toISOString(),
            expiresAt: new Date(forecastDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
            dismissible: true,
            actionable: true,
            actions: [
              { label: 'View Tasks', action: 'view_tasks' }
            ]
          });
        }
      }
    }

    return alerts;
  }

  /**
   * Check for good weather windows (positive alerts)
   */
  private checkGoodWeatherWindows(forecast: WeatherForecast[], outdoorTasks: Task[]): WeatherAlert[] {
    const alerts: WeatherAlert[] = [];
    const now = new Date();

    // Look for ideal weather days
    for (const day of forecast) {
      const forecastDate = new Date(day.date);
      const daysFromNow = Math.floor((forecastDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysFromNow < 1 || daysFromNow > 7) continue;

      // Perfect weather conditions: sunny/clear, moderate temps, low rain chance
      const isIdealWeather =
        (day.conditions === 'sunny' || day.conditions === 'clear') &&
        day.tempHigh >= 65 && day.tempHigh <= 85 &&
        day.tempLow >= 50 &&
        day.rainChance < 20 &&
        day.windSpeed < 15;

      if (isIdealWeather && outdoorTasks.length > 0) {
        // Check if there's bad weather before or after (making this window special)
        const previousDay = forecast.find(d => {
          const diff = Math.floor((new Date(d.date).getTime() - forecastDate.getTime()) / (1000 * 60 * 60 * 24));
          return diff === -1;
        });
        const nextDay = forecast.find(d => {
          const diff = Math.floor((new Date(d.date).getTime() - forecastDate.getTime()) / (1000 * 60 * 60 * 24));
          return diff === 1;
        });

        const hasBadWeatherNearby =
          (previousDay && (previousDay.rainChance > 60 || previousDay.tempHigh > 90)) ||
          (nextDay && (nextDay.rainChance > 60 || nextDay.tempHigh > 90));

        if (hasBadWeatherNearby || daysFromNow <= 3) {
          alerts.push({
            id: `good-weather-${day.date}`,
            type: 'medium',
            severity: 'info',
            title: 'Perfect Weather Window',
            message: `Ideal conditions ${this.formatDaysFromNow(daysFromNow)} (${Math.round(day.tempHigh)}°F, ${day.conditions}) - Consider scheduling ${outdoorTasks.length} outdoor task${outdoorTasks.length > 1 ? 's' : ''}`,
            icon: 'sun',
            affectedTaskIds: outdoorTasks.map(t => t.id),
            affectedTaskCount: outdoorTasks.length,
            date: day.date,
            createdAt: new Date().toISOString(),
            expiresAt: new Date(forecastDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
            dismissible: true,
            actionable: true,
            actions: [
              { label: 'View Tasks', action: 'view_tasks' },
              { label: 'Schedule', action: 'reschedule' }
            ]
          });
        }
      }
    }

    return alerts;
  }

  /**
   * Filter tasks that are outdoor/weather-sensitive
   */
  private filterOutdoorTasks(tasks: Task[]): Task[] {
    const outdoorCategories = [
      'landscaping',
      'yard',
      'exterior',
      'painting',
      'carpentry'
    ];

    return tasks.filter(task => {
      // Check category
      if (outdoorCategories.includes(task.category)) {
        return true;
      }

      // Check description for outdoor keywords
      const outdoorKeywords = ['outdoor', 'exterior', 'yard', 'garden', 'lawn', 'roof', 'gutter', 'siding', 'fence', 'deck', 'patio'];
      const description = (task.description || '').toLowerCase();
      const title = (task.title || '').toLowerCase();

      return outdoorKeywords.some(keyword =>
        description.includes(keyword) || title.includes(keyword)
      );
    });
  }

  /**
   * Get tasks scheduled near a specific date
   */
  private getTasksNearDate(tasks: Task[], targetDate: string, daysRange: number): Task[] {
    const target = new Date(targetDate);

    return tasks.filter(task => {
      if (!task.due_date) return false;

      const dueDate = new Date(task.due_date);
      const daysDiff = Math.abs((dueDate.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

      return daysDiff <= daysRange;
    });
  }

  /**
   * Format days from now into human-readable string
   */
  private formatDaysFromNow(days: number): string {
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    if (days === 2) return 'in 2 days';
    return `in ${days} days`;
  }

  /**
   * Dismiss an alert
   */
  dismissAlert(alertId: string): void {
    this.dismissedAlerts.add(alertId);
    this.saveDismissedAlerts();
  }

  /**
   * Check if alert is dismissed
   */
  private isAlertDismissed(alertId: string): boolean {
    return this.dismissedAlerts.has(alertId);
  }

  /**
   * Clear dismissed alerts (e.g., when weather changes significantly)
   */
  clearDismissedAlerts(): void {
    this.dismissedAlerts.clear();
    this.saveDismissedAlerts();
  }

  /**
   * Save dismissed alerts to localStorage
   */
  private saveDismissedAlerts(): void {
    try {
      const dismissed = Array.from(this.dismissedAlerts);
      localStorage.setItem('honeydo_dismissed_weather_alerts', JSON.stringify(dismissed));
    } catch (error) {
      console.error('[WeatherAlertService] Failed to save dismissed alerts:', error);
    }
  }

  /**
   * Load dismissed alerts from localStorage
   */
  private loadDismissedAlerts(): void {
    try {
      const stored = localStorage.getItem('honeydo_dismissed_weather_alerts');
      if (stored) {
        const dismissed = JSON.parse(stored);
        this.dismissedAlerts = new Set(dismissed);

        // Clear old dismissed alerts (older than 7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        Array.from(this.dismissedAlerts).forEach(alertId => {
          // Extract date from alert ID if possible
          const match = alertId.match(/\d{4}-\d{2}-\d{2}/);
          if (match) {
            const alertDate = new Date(match[0]);
            if (alertDate < sevenDaysAgo) {
              this.dismissedAlerts.delete(alertId);
            }
          }
        });

        this.saveDismissedAlerts();
      }
    } catch (error) {
      console.error('[WeatherAlertService] Failed to load dismissed alerts:', error);
      this.dismissedAlerts = new Set();
    }
  }

  /**
   * Get alert badge count (for UI notification badge)
   */
  getAlertBadgeCount(alerts: WeatherAlert[]): number {
    return alerts.filter(a => a.type === 'critical' || a.type === 'high').length;
  }

  /**
   * Sort alerts by priority
   */
  sortAlertsByPriority(alerts: WeatherAlert[]): WeatherAlert[] {
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    return [...alerts].sort((a, b) => priorityOrder[a.type] - priorityOrder[b.type]);
  }
}

// Export singleton instance
export const weatherAlertService = new WeatherAlertService();

export default weatherAlertService;
